import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// short, soft two-tone chime via WebAudio (no asset needed)
const playChime = () => {
    try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        [880, 1175].forEach((f, i) => {
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.type = 'sine'; o.frequency.value = f;
            o.connect(g); g.connect(ctx.destination);
            const t = ctx.currentTime + i * 0.14;
            g.gain.setValueAtTime(0.0001, t);
            g.gain.exponentialRampToValueAtTime(0.18, t + 0.02);
            g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
            o.start(t); o.stop(t + 0.24);
        });
        setTimeout(() => ctx.close(), 800);
    } catch { /* audio not allowed yet */ }
};

/**
 * SupportMessagePopup — mounted globally inside the admin panel. Polls the
 * support unread-count endpoint and pops a toast the moment a NEW customer
 * message arrives, so nothing gets missed while the operator works. Clicking
 * the toast jumps straight to the Support chat.
 *
 * It does NOT pop for messages that were already unread when the panel opened
 * (only for genuinely new arrivals during the session).
 */
const SupportMessagePopup = ({ onOpenSupport, intervalMs = 10000 }) => {
    const seenKey = useRef(null);
    const primed = useRef(false);
    const [unread, setUnread] = useState(0);

    useEffect(() => {
        let alive = true;
        let timer;
        const poll = async () => {
            try {
                const { data } = await axios.get(`${API}/admin/support/unread-count`, { withCredentials: true });
                setUnread(Number(data?.total_unread || 0));
                const latest = data?.latest_unread;
                if (latest) {
                    const key = `${latest.id}:${latest.last_message_at}`;
                    if (seenKey.current !== key) {
                        if (primed.current) {
                            playChime();
                            const who = latest.user_name || latest.user_email || 'A customer';
                            toast.custom((t) => (
                                <button
                                    type="button"
                                    onClick={() => { onOpenSupport?.(latest.id); toast.dismiss(t); }}
                                    className="w-[340px] text-left rounded-xl border border-[#FFD700]/40 bg-[#12100a] shadow-xl p-3 flex gap-3 hover:bg-[#1a1710] transition"
                                    data-testid="support-popup-toast"
                                >
                                    <div className="w-9 h-9 rounded-lg bg-[#FFD700]/15 flex items-center justify-center shrink-0">
                                        <MessageCircle className="w-5 h-5 text-[#FFD700]" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-black text-[#FFD700] truncate">New message · {who}</p>
                                        <p className="text-xs text-white/70 truncate">{latest.preview || 'Tap to open the conversation'}</p>
                                        <p className="text-[10px] text-white/40 mt-0.5">Tap to open chat →</p>
                                    </div>
                                </button>
                            ), { duration: 15000 });
                        }
                        seenKey.current = key;
                    }
                }
                primed.current = true;
            } catch {
                /* transient — retry next tick */
            }
            if (alive) timer = setTimeout(poll, intervalMs);
        };
        poll();
        return () => { alive = false; clearTimeout(timer); };
    }, [onOpenSupport, intervalMs]);

    if (!unread) return null;
    return (
        <button
            type="button"
            onClick={() => onOpenSupport?.()}
            className="fixed bottom-5 right-5 z-[9998] flex items-center gap-2 rounded-full bg-[#12100a] border border-[#FFD700]/50 shadow-xl px-4 py-2.5 hover:bg-[#1a1710] transition"
            data-testid="support-unread-badge"
            title={`${unread} unread support message${unread === 1 ? '' : 's'}`}
        >
            <MessageCircle className="w-5 h-5 text-[#FFD700]" />
            <span className="text-sm font-black text-[#FFD700]">{unread}</span>
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#FF3B30] animate-pulse" />
        </button>
    );
};

export default SupportMessagePopup;
